import React, { useMemo, useState } from "react";
import {
    LinearProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
} from "@mui/material";
import { useAtom } from "jotai";
import { periodAtomsAtom } from "../../stores/jotai/base";
import PresentationLinkTableRow from "./PresentationLinkTableRow";
import {
    PRESENTATION_LINK_TABLE_SORT_CRITERIA,
    PRESENTATION_LINK_TABLE_SORT_DIRECTION,
} from "../discovery/utils/constants";

interface SortParamState {
    direction: "asc" | "desc";
    criteria: "week";
}

const PresentationLinkTable = () => {
    const [sortParam, setSortParam] = useState<SortParamState>({
        direction: PRESENTATION_LINK_TABLE_SORT_DIRECTION.ASC,
        criteria: PRESENTATION_LINK_TABLE_SORT_CRITERIA.WEEK,
    });
    const [periodAtoms,] = useAtom(periodAtomsAtom);
    const periodAtomsWithSortOrder = periodAtoms?.map((periodAtom, index) => ({
        atom: periodAtom,
        sortOrder: index + 1,
    }));
    const isSortCriteriaWeek =
        sortParam.criteria === PRESENTATION_LINK_TABLE_SORT_CRITERIA.WEEK;

    const sortedPeriodAtoms = useMemo(() => {
        const { direction, criteria } = sortParam;
        const sortedAtoms = [...periodAtomsWithSortOrder];

        if (criteria === PRESENTATION_LINK_TABLE_SORT_CRITERIA.WEEK) {
            sortedAtoms.sort((atom1, atom2) => {
                if (direction === PRESENTATION_LINK_TABLE_SORT_DIRECTION.ASC) {
                    return atom1.sortOrder - atom2.sortOrder;
                }

                return atom2.sortOrder - atom1.sortOrder;
            });
        }

        return sortedAtoms;
    }, [periodAtoms, sortParam]);

    const handleColumnSort = (property) => (event) => {
        setSortParam((prevParam) => {
            let direction: "asc" | "desc" =
                PRESENTATION_LINK_TABLE_SORT_DIRECTION.ASC;

            if (prevParam.criteria === property) {
                direction =
                    sortParam.direction ===
                    PRESENTATION_LINK_TABLE_SORT_DIRECTION.ASC
                        ? PRESENTATION_LINK_TABLE_SORT_DIRECTION.DESC
                        : PRESENTATION_LINK_TABLE_SORT_DIRECTION.ASC;
            }

            return {
                criteria: property,
                direction,
            };
        });
    };

    if (!periodAtoms || periodAtoms?.length === 0) {
        return <LinearProgress />; 
    }


    return (
        <TableContainer
            component={Paper}
            sx={{
                border: "1px solid #c2c2c2",
                flexGrow: 1,
                maxHeight: "calc(100vh - 150px)",
                overflow: "auto", 
            }}
        >
            <Table
                size="small"
                stickyHeader={true}
                sx={{
                    ".MuiTableCell-root": {
                        paddingX: "24px",
                    },
                }}
            >
                <TableHead>
                    <TableRow>
                        <TableCell sortDirection={sortParam?.direction}>
                            <TableSortLabel
                                active={isSortCriteriaWeek}
                                direction={sortParam.direction}
                                onClick={handleColumnSort(
                                    PRESENTATION_LINK_TABLE_SORT_CRITERIA.WEEK
                                )}
                            >
                                Week
                            </TableSortLabel>
                        </TableCell>
                        <TableCell>View</TableCell>
                        <TableCell align="center">Link</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {sortedPeriodAtoms.map((periodAtomWithSortOrder) => (
                        <PresentationLinkTableRow
                            key={`${periodAtomWithSortOrder?.atom}`}
                            periodAtom={periodAtomWithSortOrder?.atom}
                            periodIndex={periodAtomWithSortOrder?.sortOrder}
                        />
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default PresentationLinkTable;
